import React, { useState, useEffect, useRef } from 'react';
import { ChevronLeft, ChevronRight, Play, Pause, Volume2, VolumeX } from 'lucide-react';

// Images des 10 étapes du change coucher - Photos réalistes avec même auxiliaire
// Ordre: lavage mains, préparation, soin de l'enfant, nettoyage table à la fin
const CHANGE_STEPS = [
  {
    id: 1,
    title: "Étape 1 : Se laver les mains",
    description: "Lavage soigneux des mains au savon pendant 30 secondes minimum AVANT le soin",
    image: "https://static.prod-images.emergentagent.com/jobs/6dd9a748-1781-47e8-95b8-945bc0962496/images/cf91aacce22b309e1f6bcbc864c50d5f4e23193646cea91bcac88e174813610a.png"
  },
  {
    id: 2,
    title: "Étape 2 : Préparer le matériel",
    description: "Couche propre, gant et savon adapté, poubelle à pédale, vêtements si besoin - TOUT à portée de main",
    image: "https://static.prod-images.emergentagent.com/jobs/6dd9a748-1781-47e8-95b8-945bc0962496/images/2a8877db4317eca66a520de2730b40a2602a4cf700980950c8ccc9f7dffb7132.png"
  },
  {
    id: 3,
    title: "Étape 3 : Expliquer à l'enfant",
    description: "Parler à l'enfant, lui expliquer ce que l'on va faire avec douceur",
    image: "https://static.prod-images.emergentagent.com/jobs/6dd9a748-1781-47e8-95b8-945bc0962496/images/7f39d4ecc884eafab46d64acf0eda30b9bd6668c26086741e7d4784e9127b5a4.png"
  },
  {
    id: 4,
    title: "Étape 4 : Technique de l'enroulé",
    description: "Tourner doucement l'enfant sur le côté (ne plus lever les jambes en l'air)",
    image: "https://static.prod-images.emergentagent.com/jobs/6dd9a748-1781-47e8-95b8-945bc0962496/images/59974e95018e039f1733ddf278d989f1c3ec18371e91bc37744669f950e9119f.png"
  },
  {
    id: 5,
    title: "Étape 5 : Nettoyer d'avant vers l'arrière",
    description: "Nettoyer délicatement de l'avant vers l'arrière pour éviter les infections",
    image: "https://static.prod-images.emergentagent.com/jobs/6dd9a748-1781-47e8-95b8-945bc0962496/images/e025373afc5b5a16bf942bbfe3ebff87b52ff4d3e603681948fc8b9964f7f2c4.png"
  },
  {
    id: 6,
    title: "Étape 6 : Mettre la couche propre",
    description: "Glisser la couche propre sous le bassin et l'ajuster confortablement",
    image: "https://static.prod-images.emergentagent.com/jobs/6dd9a748-1781-47e8-95b8-945bc0962496/images/c86a5558de8e5d0bff62fb22f90d87e17587315f9d3f242fc9762f821b404797.png"
  },
  {
    id: 7,
    title: "Étape 7 : Rhabiller l'enfant",
    description: "Remettre les vêtements et réinstaller l'enfant confortablement",
    image: "https://static.prod-images.emergentagent.com/jobs/6dd9a748-1781-47e8-95b8-945bc0962496/images/2554aa36235928f7f57ee0dc49b1b345dc5d89d10900c6d460666464724ea89a.png"
  },
  {
    id: 8,
    title: "Étape 8 : Nettoyer la table (détourage)",
    description: "Nettoyer le contour de la table à langer avec un désinfectant adapté",
    image: "https://static.prod-images.emergentagent.com/jobs/6dd9a748-1781-47e8-95b8-945bc0962496/images/ef8da9224fc25d7f21163a8df877c60dad9a9e3283fcad966f7de973c3049a8c.png"
  },
  {
    id: 9,
    title: "Étape 9 : Nettoyer la surface (godille)",
    description: "Nettoyer la surface en S (technique de la godille) pour une désinfection complète",
    image: "https://static.prod-images.emergentagent.com/jobs/6dd9a748-1781-47e8-95b8-945bc0962496/images/dfd2ba81318c4a2524c60fabc2484828393e9b8c6c8cdedf6f10c1d36e941a20.png"
  },
  {
    id: 10,
    title: "Étape 10 : Se laver les mains APRÈS",
    description: "Lavage des mains obligatoire après le soin pour l'hygiène de tous",
    image: "https://static.prod-images.emergentagent.com/jobs/6dd9a748-1781-47e8-95b8-945bc0962496/images/33e8abb2574b2c9e2aca17aa5f393358e5b67341bf73781194a3339cecbd1f64.png"
  }
];

// Logo APDR complet
const APDR_LOGO_FULL = "https://customer-assets.emergentagent.com/job_0bdb7109-8029-4d81-a486-71beb721288a/artifacts/o3wx8hx1_logo%20apdr%20formation.png";

const ChangeCarousel = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const audioRef = useRef(null);
  const intervalRef = useRef(null);

  // Musique douce de fond
  const BACKGROUND_MUSIC = "https://cdn.pixabay.com/audio/2022/10/25/audio_850edcfd29.mp3";

  useEffect(() => {
    if (isPlaying) {
      intervalRef.current = setInterval(() => {
        setCurrentStep((prev) => (prev + 1) % CHANGE_STEPS.length);
      }, 5000);
    } else {
      clearInterval(intervalRef.current);
    }
    return () => clearInterval(intervalRef.current);
  }, [isPlaying]);

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = 0.3;
      if (isPlaying && !isMuted) {
        audioRef.current.play().catch(() => {});
      } else {
        audioRef.current.pause();
      }
    }
  }, [isPlaying, isMuted]);

  const handlePrev = () => {
    setCurrentStep((prev) => (prev - 1 + CHANGE_STEPS.length) % CHANGE_STEPS.length);
  };

  const handleNext = () => {
    setCurrentStep((prev) => (prev + 1) % CHANGE_STEPS.length);
  };

  const step = CHANGE_STEPS[currentStep];

  // Couleur thématique - Violet/Mauve pour le change (différent du rose biberon)
  const themeColor = '#8B5CF6';
  const themeColorLight = '#C4B5FD';
  const themeColorDark = '#7C3AED';

  return (
    <div 
      className="relative rounded-2xl overflow-hidden shadow-2xl border-2"
      style={{ minHeight: '480px', borderColor: themeColorLight }}
      data-testid="change-carousel"
    >
      <audio ref={audioRef} loop>
        <source src={BACKGROUND_MUSIC} type="audio/mpeg" />
      </audio>

      {/* Fond avec logo APDR */}
      <div 
        className="absolute inset-0"
        style={{
          backgroundImage: `url(${APDR_LOGO_FULL})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          opacity: 0.08
        }}
      />

      {/* Overlay */}
      <div 
        className="absolute inset-0"
        style={{
          background: `linear-gradient(135deg, rgba(255,255,255,0.92) 0%, rgba(237,233,254,0.88) 100%)`
        }}
      />

      {/* Header */}
      <div 
        className="relative z-10 text-white p-4"
        style={{ background: `linear-gradient(to right, ${themeColor}, ${themeColorDark})` }}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-white p-1 shadow-lg">
              <img src={APDR_LOGO_FULL} alt="APDR" className="w-full h-full object-contain" />
            </div>
            <div>
              <h3 className="font-bold text-lg">Formation APDR de la VAE</h3>
              <p className="text-purple-100 text-sm">Le change coucher - 10 étapes</p>
            </div>
          </div>
          <span className="bg-white/20 px-3 py-1 rounded-full text-sm font-semibold">
            {currentStep + 1} / {CHANGE_STEPS.length}
          </span>
        </div>
      </div>

      {/* Contenu */}
      <div className="relative z-10 p-6">
        <div className="flex flex-col md:flex-row gap-6 items-center">
          {/* Image */}
          <div className="flex-1 flex justify-center">
            <div className="relative">
              <img 
                src={step.image} 
                alt={step.title}
                className="w-72 h-72 object-cover rounded-2xl shadow-xl border-4 border-white"
                key={step.id}
                style={{ animation: 'fadeIn 0.5s ease-in-out' }}
              />
              <div 
                className="absolute -top-3 -left-3 text-white w-12 h-12 rounded-full flex items-center justify-center font-bold text-xl shadow-lg border-2 border-white"
                style={{ background: `linear-gradient(to bottom right, ${themeColor}, ${themeColorDark})` }}
              >
                {step.id}
              </div>
            </div>
          </div>

          {/* Texte */}
          <div className="flex-1 text-center md:text-left">
            <h2 className="text-2xl font-bold mb-3" style={{ color: themeColor }}>{step.title}</h2>
            <p className="text-gray-700 text-lg mb-6 bg-white/60 p-3 rounded-lg">{step.description}</p>

            {/* Indicateurs */}
            <div className="flex gap-2 justify-center md:justify-start flex-wrap">
              {CHANGE_STEPS.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentStep(index)}
                  className={`h-3 rounded-full transition-all duration-300`}
                  style={{
                    backgroundColor: index === currentStep ? themeColor : themeColorLight,
                    width: index === currentStep ? '2rem' : '0.75rem'
                  }}
                  data-testid={`change-carousel-dot-${index}`}
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Contrôles */}
      <div className="relative z-10 bg-white/90 backdrop-blur-sm p-4 flex items-center justify-between border-t" style={{ borderColor: themeColorLight }}>
        <button
          onClick={handlePrev}
          className="flex items-center gap-2 px-4 py-2 rounded-full transition-all font-medium"
          style={{ backgroundColor: `${themeColorLight}40`, color: themeColor }}
          onMouseEnter={(e) => e.target.style.backgroundColor = themeColorLight}
          onMouseLeave={(e) => e.target.style.backgroundColor = `${themeColorLight}40`}
          data-testid="change-carousel-prev"
        >
          <ChevronLeft size={20} />
          Précédent
        </button>

        <div className="flex items-center gap-3">
          <button
            onClick={() => setIsPlaying(!isPlaying)}
            className={`w-14 h-14 rounded-full flex items-center justify-center transition-all shadow-lg`}
            style={{
              backgroundColor: isPlaying ? themeColor : 'white',
              color: isPlaying ? 'white' : themeColor,
              border: isPlaying ? 'none' : `2px solid ${themeColor}`
            }}
            data-testid="change-carousel-play"
          >
            {isPlaying ? <Pause size={24} /> : <Play size={24} className="ml-1" />}
          </button>

          <button
            onClick={() => setIsMuted(!isMuted)}
            className={`w-10 h-10 rounded-full flex items-center justify-center transition-all`}
            style={{
              backgroundColor: isMuted ? '#E5E7EB' : `${themeColorLight}40`,
              color: isMuted ? '#6B7280' : themeColor
            }}
            data-testid="change-carousel-mute"
          >
            {isMuted ? <VolumeX size={18} /> : <Volume2 size={18} />}
          </button>
        </div>

        <button
          onClick={handleNext}
          className="flex items-center gap-2 px-4 py-2 text-white rounded-full transition-all font-medium"
          style={{ backgroundColor: themeColor }}
          onMouseEnter={(e) => e.target.style.backgroundColor = themeColorDark}
          onMouseLeave={(e) => e.target.style.backgroundColor = themeColor}
          data-testid="change-carousel-next"
        >
          Suivant
          <ChevronRight size={20} />
        </button>
      </div>

      {/* Barre de progression */}
      <div className="relative z-10 h-2" style={{ backgroundColor: themeColorLight }}>
        <div 
          className="h-full transition-all duration-300"
          style={{ 
            background: `linear-gradient(to right, ${themeColorLight}, ${themeColor})`,
            width: `${((currentStep + 1) / CHANGE_STEPS.length) * 100}%` 
          }}
        />
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: scale(0.95); }
          to { opacity: 1; transform: scale(1); }
        }
      `}</style>
    </div>
  );
};

export default ChangeCarousel;
